import { type FormEvent, useEffect, useMemo, useState } from 'react'
import {
  analyzeHealRequest, downloadHealFix, executeFixPackageLocally, getHealModelConfiguration,
  getHealModelOptions, listHealRequests, markHealRequestsFixed, saveHealModelConfiguration,
  submitHealRequest, type HealModelConfiguration, type HealProvider, type HealRequest,
} from './healApi'

const terminal = new Set(['DIAGNOSED', 'DIAGNOSIS_FAILED', 'FAILED', 'CANCELLED', 'FIXED', 'FIX_PUSHED'])

function label(item: HealRequest) {
  return `HEAL-${String(item.sequence_number).padStart(5, '0')}`
}

export function HealPage({ localMode = false }: { localMode?: boolean }) {
  const [items, setItems] = useState<HealRequest[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [description, setDescription] = useState('')
  const [expectedActual, setExpectedActual] = useState('')
  const [steps, setSteps] = useState('')
  const [severity, setSeverity] = useState('Medium')
  const [correlationId, setCorrelationId] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [modelConfig, setModelConfig] = useState<HealModelConfiguration | null>(null)
  const [providers, setProviders] = useState<HealProvider[]>([])
  const [modelBusy, setModelBusy] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'open' | 'fixed'>('open')
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [markBusy, setMarkBusy] = useState(false)
  const [fixPackageFile, setFixPackageFile] = useState<File | null>(null)
  const [devBranchUrl, setDevBranchUrl] = useState('')
  const [qaBranch, setQaBranch] = useState('')
  const [executeBusy, setExecuteBusy] = useState(false)

  const isFixed = (item: HealRequest) => item.status === 'FIXED' || item.status === 'FIX_PUSHED'
  const filteredItems = useMemo(
    () => items.filter(item => (statusFilter === 'fixed' ? isFixed(item) : !isFixed(item))),
    [items, statusFilter],
  )
  const allFilteredChecked = filteredItems.length > 0 && filteredItems.every(item => checkedIds.has(item.id))
  const selected = useMemo(() => items.find(item => item.id === selectedId) || null, [items, selectedId])

  async function refresh() {
    const next = await listHealRequests()
    setItems(next)
    setSelectedId(current => current || next[0]?.id || null)
  }

  useEffect(() => {
    listHealRequests().then(next => {
      setItems(next)
      setSelectedId(next[0]?.id || null)
    }).catch(reason => setError(reason instanceof Error ? reason.message : 'Heal History could not be loaded.'))
    Promise.all([getHealModelConfiguration(), getHealModelOptions()]).then(([config, options]) => {
      setModelConfig(config); setProviders(options)
    }).catch(reason => setError(reason instanceof Error ? reason.message : 'Heal model configuration could not be loaded.'))
  }, [])

  useEffect(() => {
    if (!items.some(item => !terminal.has(item.status) && !['NEW', 'DIAGNOSED', 'DIAGNOSIS_FAILED'].includes(item.status))) return
    const timer = window.setInterval(() => void refresh(), 5000)
    return () => window.clearInterval(timer)
  }, [items])

  function replace(updated: HealRequest) {
    setItems(current => [updated, ...current.filter(item => item.id !== updated.id)])
    setSelectedId(updated.id)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true); setError('')
    try {
      const form = new FormData()
      form.set('description', description.trim())
      form.set('expected_actual', expectedActual.trim())
      form.set('reproduction_steps', steps.trim())
      form.set('severity', severity)
      form.set('correlation_id', correlationId.trim())
      form.set('failing_url', window.location.href)
      if (attachment) form.set('attachment', attachment)
      const created = await submitHealRequest(form)
      replace(created)
      const analyzed = await analyzeHealRequest(created.id)
      replace(analyzed)
      setDescription(''); setExpectedActual(''); setSteps(''); setCorrelationId(''); setAttachment(null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Heal analysis failed.')
    } finally { setBusy(false) }
  }

  async function saveModel() {
    if (!modelConfig) return
    setModelBusy(true); setError('')
    try { setModelConfig(await saveHealModelConfiguration(modelConfig)) }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Heal model configuration could not be saved.') }
    finally { setModelBusy(false) }
  }

  async function downloadFix(item: HealRequest) {
    setBusy(true); setError('')
    try { await downloadHealFix(item.id, item.sequence_number); await refresh() }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'The fix package could not be downloaded.') }
    finally { setBusy(false) }
  }

  function toggleChecked(id: string) {
    setCheckedIds(current => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setCheckedIds(current => {
      if (allFilteredChecked) {
        const next = new Set(current)
        filteredItems.forEach(item => next.delete(item.id))
        return next
      }
      return new Set([...current, ...filteredItems.map(item => item.id)])
    })
  }

  async function markSelectedFixed() {
    if (checkedIds.size === 0) return
    setMarkBusy(true); setError('')
    try {
      const updated = await markHealRequestsFixed([...checkedIds])
      setItems(current => {
        const byId = new Map(updated.map(item => [item.id, item]))
        return current.map(item => byId.get(item.id) || item)
      })
      setCheckedIds(new Set())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The selected Heal requests could not be marked fixed.')
    } finally { setMarkBusy(false) }
  }

  async function executeFixPackage() {
    if (!selected || !fixPackageFile || !devBranchUrl.trim() || !qaBranch.trim()) return
    setExecuteBusy(true); setError('')
    try {
      const updated = await executeFixPackageLocally(selected.id, fixPackageFile, devBranchUrl.trim(), qaBranch.trim())
      replace(updated)
      setFixPackageFile(null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The fix package could not be run.')
    } finally { setExecuteBusy(false) }
  }

  const selectedProvider = providers.find(provider => provider.provider_id === modelConfig?.provider)

  return <main className="heal-shell">
    <header className="heal-header"><div><span className="heal-mark">capSpire</span><h1>Heal</h1><p>Private error diagnosis and portable developer fix packages</p></div><span className="heal-private">Read-only in production</span></header>
    {error && <div className="heal-error" role="alert">{error}</div>}
    <div className="heal-layout">
      <section className="heal-panel heal-new" aria-labelledby="new-heal-title">
        <h2 id="new-heal-title">Diagnose an error</h2>
        <p>Only the error description is required. Evidence is stored with the Heal record.</p>
        {modelConfig && <div className="heal-model-config" aria-label="Heal model configuration">
          <strong>Heal AI model</strong>
          <label>Provider<select aria-label="Heal provider" value={modelConfig.provider} onChange={event => {
            const provider = providers.find(item => item.provider_id === event.target.value)
            setModelConfig({ ...modelConfig, provider: event.target.value as HealModelConfiguration['provider'], model: provider?.models[0]?.id || '' })
          }}>{providers.filter(provider => provider.configured).map(provider => <option key={provider.provider_id} value={provider.provider_id}>{provider.name}</option>)}</select></label>
          <label>Model<select aria-label="Heal model" value={modelConfig.model} onChange={event => setModelConfig({ ...modelConfig, model: event.target.value })}>{selectedProvider?.models.map(model => <option key={model.id} value={model.id}>{model.label}</option>)}</select></label>
          <button type="button" onClick={() => void saveModel()} disabled={modelBusy || !modelConfig.model || modelConfig.provider === 'local_cli'}>{modelBusy ? 'Saving…' : modelConfig.provider === 'local_cli' ? 'Selected by local launcher' : 'Save model'}</button>
          <small>This selection is used for diagnosis and embedded in every downloaded fix package.</small>
        </div>}
        <form onSubmit={submit}>
          <label>Error description <b>*</b><textarea required minLength={5} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what failed and what you observed." /></label>
          <label>Expected versus actual behavior<textarea value={expectedActual} onChange={e => setExpectedActual(e.target.value)} /></label>
          <label>Reproduction steps<textarea value={steps} onChange={e => setSteps(e.target.value)} /></label>
          <div className="heal-form-row"><label>Severity<select value={severity} onChange={e => setSeverity(e.target.value)}><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select></label><label>Correlation ID<input value={correlationId} onChange={e => setCorrelationId(e.target.value)} /></label></div>
          <label>Screenshot or log <small>Optional · PNG, JPEG, WebP, text or JSON · maximum 5 MB</small><input type="file" accept="image/png,image/jpeg,image/webp,text/plain,application/json" onChange={e => setAttachment(e.target.files?.[0] || null)} /></label>
          <button className="heal-primary" disabled={busy || description.trim().length < 5}>{busy ? 'Analyzing…' : 'Analyze'}</button>
        </form>
      </section>
      <section className="heal-panel heal-history" aria-labelledby="heal-history-title">
        <div className="heal-section-title"><div><h2 id="heal-history-title">Heal History</h2><p>{filteredItems.length} of {items.length} retained record{items.length === 1 ? '' : 's'}</p></div><button onClick={() => void refresh()} disabled={busy}>Refresh</button></div>
        <div className="heal-filter-row">
          <div className="heal-status-filter" role="tablist" aria-label="Filter by status">
            <button type="button" role="tab" aria-selected={statusFilter === 'open'} className={statusFilter === 'open' ? 'active' : ''} onClick={() => setStatusFilter('open')}>Open</button>
            <button type="button" role="tab" aria-selected={statusFilter === 'fixed'} className={statusFilter === 'fixed' ? 'active' : ''} onClick={() => setStatusFilter('fixed')}>Fixed</button>
          </div>
          {filteredItems.length > 0 && <label className="heal-select-all"><input type="checkbox" checked={allFilteredChecked} onChange={toggleSelectAll} /> Select all</label>}
        </div>
        {checkedIds.size > 0 && <div className="heal-bulk-actions"><span>{checkedIds.size} selected</span><button type="button" onClick={() => void markSelectedFixed()} disabled={markBusy}>{markBusy ? 'Marking…' : 'Mark selected as fixed'}</button></div>}
        {filteredItems.length === 0 ? <p className="heal-empty">No {statusFilter} Heal records.</p> : <div className="heal-list">{filteredItems.map(item => <div key={item.id} className={`heal-list-row ${selectedId === item.id ? 'selected' : ''}`}>
          <input type="checkbox" aria-label={`Select ${label(item)}`} checked={checkedIds.has(item.id)} onChange={() => toggleChecked(item.id)} onClick={event => event.stopPropagation()} />
          <button onClick={() => setSelectedId(item.id)}><strong>{label(item)}</strong><span className={`heal-status status-${item.status.toLowerCase()}`}>{item.status.replaceAll('_', ' ')}</span><p>{item.description}</p><small>{new Date(item.created_at).toLocaleString()} · {item.source.replaceAll('_', ' ')}</small></button>
        </div>)}</div>}
      </section>
      <section className="heal-panel heal-detail" aria-labelledby="heal-detail-title">
        {!selected ? <p className="heal-empty">Select a Heal record to inspect it.</p> : <>
          <div className="heal-section-title"><div><h2 id="heal-detail-title">{label(selected)}</h2><p>{selected.environment} · {selected.severity} · {selected.occurrence_count} occurrence{selected.occurrence_count === 1 ? '' : 's'}</p></div><span className={`heal-status status-${selected.status.toLowerCase()}`}>{selected.status.replaceAll('_', ' ')}</span></div>
          <h3>Reported error</h3><div className="heal-text">{selected.description}</div>
          {selected.attachment_name && <p><strong>Evidence:</strong> {selected.attachment_name}</p>}
          {selected.recommended_model && <div className="heal-model"><strong>Diagnosis model</strong><span>{selected.recommended_provider} / {selected.recommended_model}</span><small>{selected.model_reason}</small></div>}
          {selected.diagnosis && <><h3>Diagnosis and concrete facts</h3><div className="heal-text diagnosis">{selected.diagnosis}</div></>}
          {selected.remediation && <><h3>Resolution and code guidance</h3><div className="heal-text diagnosis">{selected.remediation}</div></>}
          {selected.status === 'DIAGNOSED' && <button className="heal-primary" onClick={() => void downloadFix(selected)} disabled={busy}>{busy ? 'Preparing package…' : 'Download fix'}</button>}
          {selected.pull_request_url && <p className="heal-pr-link"><strong>Draft PR:</strong> <a href={selected.pull_request_url} target="_blank" rel="noreferrer">{selected.branch_name}</a></p>}
          {localMode && <div className="heal-execute-local" aria-label="Run a fix package locally">
            <h3>Run a fix package on this machine</h3>
            <p>Upload a downloaded <code>HEAL-*-fix.zip</code> and run its guarded runner directly -- no manual extraction. It only ever pushes an isolated branch and opens a draft PR; it never deploys or merges.</p>
            <label>Fix package (.zip)<input type="file" accept=".zip" onChange={e => setFixPackageFile(e.target.files?.[0] || null)} /></label>
            <div className="heal-form-row">
              <label>Dev branch URL<input value={devBranchUrl} onChange={e => setDevBranchUrl(e.target.value)} placeholder="https://dev.azure.com/.../_git/repo?version=GBdev" /></label>
              <label>QA branch<input value={qaBranch} onChange={e => setQaBranch(e.target.value)} placeholder="qa/2026-08" /></label>
            </div>
            <button type="button" className="heal-primary" onClick={() => void executeFixPackage()} disabled={executeBusy || !fixPackageFile || !devBranchUrl.trim() || !qaBranch.trim()}>{executeBusy ? 'Running…' : 'Upload and run'}</button>
          </div>}
          {selected.failure_reason && <div className="heal-error">{selected.failure_reason}</div>}
          <h3>Timeline</h3><ol className="heal-timeline">{selected.events.map(event => <li key={event.id}><span></span><div><strong>{event.message}</strong><small>{event.actor} · {new Date(event.created_at).toLocaleString()}</small></div></li>)}</ol>
        </>}
      </section>
    </div>
  </main>
}
